/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, ShieldCheck, GraduationCap, Upload, Check, Briefcase, Paperclip, X, DollarSign, Crown } from 'lucide-react';
import { api } from '../api';
import { useLiqAuth } from '../auth';
import { AgentAvatar, clearPhotoCache } from '../components/photo';
import { withDayDividers } from '../components/dateUtils';
import { RichText } from '../components/richText';

const DayDivider = ({ label }) => (
  <div className="flex items-center gap-3 my-2 select-none" data-testid="day-divider">
    <div className="flex-1 h-px bg-white/[0.05]" />
    <span className="text-[10px] uppercase tracking-[0.22em] text-amber-200/50 bg-white/[0.02] border border-white/[0.06] rounded-full px-3 py-0.5">{label}</span>
    <div className="flex-1 h-px bg-white/[0.05]" />
  </div>
);

// ---------- Photo Upload (inline, for the profile side) ----------
function PhotoUploader({ agent, onDone }) {
  const { user } = useLiqAuth();
  const canUpload = user.id === agent.id || user.role === 'Legal IQ Lead';
  const canVerify = user.role === 'Legal IQ Lead';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!canUpload) return null;
  const upload = async (file) => {
    setBusy(true); setErr('');
    try {
      const init = await api.post(`/employees/${agent.id}/photo/init-upload`, { contentType: file.type, sizeBytes: file.size });
      const putRes = await fetch(init.data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!putRes.ok) throw new Error(`S3 upload rejected (${putRes.status})`);
      await new Promise((r) => setTimeout(r, 700));
      const fin = await api.post(`/employees/${agent.id}/photo/finalize`, { s3Key: init.data.s3Key, mediaId: init.data.mediaId, contentType: file.type });
      clearPhotoCache(agent.id);
      onDone(fin.data);
    } catch (x) { setErr(x?.response?.data?.detail || x.message || 'Upload failed'); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setErr('');
    try { const r = await api.post(`/employees/${agent.id}/photo/verify`); clearPhotoCache(agent.id); onDone(r.data); }
    catch (x) { setErr(x?.response?.data?.detail || 'Verify failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-300 hover:text-amber-200 border border-white/[0.08] hover:border-amber-500/30 rounded-full px-3 py-1.5 cursor-pointer">
          <Upload size={12} strokeWidth={2} />{agent.photo?.s3Key ? 'Replace photo' : 'Upload photo'}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={busy}
                 onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
        {canVerify && agent.photo?.s3Key && !agent.photo?.verified && (
          <button onClick={verify} disabled={busy}
            className="inline-flex items-center gap-1.5 text-[11px] text-emerald-950 bg-emerald-400 hover:bg-emerald-300 rounded-full px-3 py-1.5 font-semibold">
            <Check size={12} strokeWidth={3} /> Verify
          </button>
        )}
      </div>
      {err && <div className="text-rose-300 text-[11px] bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1">{err}</div>}
      {busy && <div className="text-amber-200/70 text-[11px]">Uploading…</div>}
    </div>
  );
}

// ---------- Agent list group + item ----------
function AgentListItem({ agent, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`group w-full text-left px-3 py-3 flex items-center gap-3 transition-all border-l-2 ${
        active ? 'bg-gradient-to-r from-amber-500/10 to-transparent border-amber-400/60' : 'border-transparent hover:bg-white/[0.02]'
      }`}
      data-testid={`agent-item-${agent.id}`}>
      <AgentAvatar agent={agent} size={44} ring={active} />
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] truncate ${active ? 'text-amber-50' : 'text-slate-100'}`}>{agent.fullName}</div>
        <div className="text-[11px] text-slate-500 truncate">{agent.designation}</div>
      </div>
    </button>
  );
}

// ---------- Chat window ----------
function ChatWindow({ agent, onAgentUpdated }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [full, setFull] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [matters, setMatters] = useState([]);
  const [attached, setAttached] = useState([]);
  const [showMatterPicker, setShowMatterPicker] = useState(false);
  const endRef = useRef(null);

  const isBiz = (full || agent).agentDomain === 'business';

  const load = async () => {
    const requests = [api.get(`/agent-chats/${agent.id}`)];
    if (!isBiz) requests.push(api.get(`/agent-chats/${agent.id}/matters`));
    const [r, mr] = await Promise.all(requests);
    setFull(r.data.agent);
    setMsgs(r.data.messages);
    setMatters(mr ? mr.data.items : []);
  };
  useEffect(() => { setAttached([]); load(); }, [agent.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs.length]);

  const toggleAttach = (mid) => setAttached((cur) => cur.includes(mid) ? cur.filter((x) => x !== mid) : [...cur, mid]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    const draft = text; const ctxIds = [...attached];
    setText('');
    setMsgs((m) => [...m, { id: `t-${Date.now()}`, senderKind: 'human', text: draft, at: new Date().toISOString(), matterContextIds: ctxIds }]);
    setMsgs((m) => [...m, { id: 'typing', senderKind: 'agent', text: '…', at: new Date().toISOString(), typing: true }]);
    try {
      const r = await api.post(`/agent-chats/${agent.id}/message`, { text: draft, matterContextIds: ctxIds });
      setMsgs((m) => m.filter((x) => x.id !== 'typing' && !x.id.startsWith('t-')).concat([r.data.userMessage, r.data.agentReply]));
    } catch (err) {
      setMsgs((m) => m.filter((x) => x.id !== 'typing'));
      alert(err?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const a = full || agent;
  const attachedMatters = matters.filter((m) => attached.includes(m.id));

  return (
    <div className="relative flex flex-col h-[calc(100vh-11rem)] rounded-2xl overflow-hidden border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl"
         style={{ boxShadow: '0 24px 60px -20px rgba(0,0,0,0.6)' }}
         data-testid="chat-window">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.06] bg-gradient-to-b from-white/[0.03] to-transparent">
        <AgentAvatar agent={a} size={52} ring glow />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-slate-50 font-medium text-[15px]">{a.fullName}</span>
            {a.agentTier === 'lead' ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-200 bg-amber-500/15 border border-amber-500/40 rounded-full px-2 py-0.5">
                <Crown size={10} strokeWidth={2.5} /> Lead
              </span>
            ) : a.agentDomain === 'business' ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-sky-300 bg-sky-500/10 border border-sky-500/30 rounded-full px-2 py-0.5">Business</span>
            ) : a.approvalStatus === 'Approved' ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">
                <ShieldCheck size={10} strokeWidth={2.5} /> Approved
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
                <GraduationCap size={10} strokeWidth={2.5} /> {a.capabilityLevel}
              </span>
            )}
          </div>
          <div className="text-slate-400 text-[12px] mt-0.5">{a.designation}</div>
        </div>
        <button onClick={() => setShowProfile((s) => !s)}
          className="text-[11px] text-slate-400 hover:text-amber-200 border border-white/[0.08] hover:border-amber-500/30 rounded-full px-3 py-1.5"
          data-testid="chat-toggle-profile">
          {showProfile ? 'Hide profile' : 'View profile'}
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-3"
               style={{ background: 'radial-gradient(600px 400px at 90% -10%, rgba(230,199,109,0.04), transparent), radial-gradient(500px 400px at -10% 100%, rgba(16,185,129,0.03), transparent)' }}
               data-testid="chat-messages">
            {msgs.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center text-slate-500 gap-2">
                <div className="text-sm text-slate-400">Start chatting with {a.fullName.split(' ')[0]}</div>
                <div className="text-[11px] text-slate-600 max-w-xs">
                  {isBiz ? `${a.designation} — no matter workflow. Ask about ${a.role.toLowerCase()}.`
                         : `${a.designation} · Attach a matter to give her full company + matter context.`}
                </div>
              </div>
            )}
            {withDayDividers(msgs).map((entry) => {
              if (entry.type === 'day') return <DayDivider key={entry.key} label={entry.label} />;
              const m = entry.msg;
              const isHuman = m.senderKind === 'human';
              const ctxMatters = (m.matterContextIds || []).map((id) => matters.find((x) => x.id === id)).filter(Boolean);
              return (
                <div key={m.id} className={`flex gap-2 ${isHuman ? 'justify-end' : 'justify-start'} animate-[fadeIn_0.25s_ease-out]`}>
                  {!isHuman && <AgentAvatar agent={a} size={30} />}
                  <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed shadow-lg ${
                    isHuman
                      ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-emerald-50 rounded-br-sm'
                      : 'bg-white/[0.04] text-slate-100 border border-white/[0.06] rounded-bl-sm backdrop-blur'
                  }`}>
                    {!isHuman && <div className="text-[10px] font-medium text-amber-200/80 mb-0.5">{a.fullName.split(' ')[0]}</div>}
                    {isHuman && ctxMatters.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        {ctxMatters.map((cm) => (
                          <span key={cm.id} className="inline-flex items-center gap-1 text-[9.5px] bg-emerald-900/40 border border-emerald-300/30 text-emerald-100 rounded-full px-2 py-0.5">
                            <Briefcase size={8} /> <span className="font-mono">{cm.matterCode}</span>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap"><RichText text={m.text} /></div>
                    <div className={`text-[10px] mt-1 ${isHuman ? 'text-emerald-100/70 text-right' : 'text-slate-500'}`}>
                      {m.typing ? (
                        <span className="inline-flex gap-1">
                          <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" />
                          <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.15s' }} />
                          <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.3s' }} />
                        </span>
                      ) : new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="border-t border-white/[0.06] bg-white/[0.02] backdrop-blur">
            {attachedMatters.length > 0 && (
              <div className="px-4 pt-3 flex flex-wrap gap-1.5" data-testid="attached-matters">
                {attachedMatters.map((m) => (
                  <span key={m.id} className="inline-flex items-center gap-1.5 text-[11px] bg-amber-500/10 border border-amber-500/30 text-amber-100 rounded-full pl-2.5 pr-1 py-1">
                    <Briefcase size={11} /> <span className="font-mono">{m.matterCode}</span>
                    <button type="button" onClick={() => toggleAttach(m.id)} className="w-4 h-4 rounded-full bg-amber-500/20 hover:bg-amber-500/30 flex items-center justify-center"><X size={9} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="px-4 py-3 flex gap-2 relative">
              {!isBiz && (
                <div className="relative">
                  <button type="button" onClick={() => setShowMatterPicker((s) => !s)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                      attachedMatters.length > 0
                        ? 'bg-amber-500/15 border-amber-500/40 text-amber-200'
                        : 'bg-white/[0.03] border-white/[0.08] text-slate-400 hover:text-amber-200 hover:border-amber-500/30'
                    }`}
                    title="Attach matter context"
                    data-testid="attach-matter-btn"><Paperclip size={16} /></button>
                  {showMatterPicker && (
                    <div className="absolute bottom-12 left-0 z-20 w-80 rounded-xl border border-white/[0.08] bg-slate-900/95 backdrop-blur-xl shadow-2xl overflow-hidden" data-testid="matter-picker">
                      <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-amber-200/70 border-b border-white/[0.06]">
                        Attach matter as context ({matters.length})
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {matters.length === 0 ? (
                          <div className="p-4 text-slate-500 text-[12px] italic text-center">
                            {a.fullName.split(' ')[0]} isn&apos;t assigned to any matter yet.
                          </div>
                        ) : matters.map((m) => {
                          const on = attached.includes(m.id);
                          return (
                            <button key={m.id} type="button" onClick={() => toggleAttach(m.id)}
                              className={`w-full text-left px-3 py-2.5 flex items-start gap-2 border-b border-white/[0.04] last:border-0 hover:bg-white/[0.03] ${on ? 'bg-amber-500/5' : ''}`}
                              data-testid={`matter-pick-${m.id}`}>
                              <div className={`w-4 h-4 mt-0.5 rounded border shrink-0 flex items-center justify-center ${on ? 'bg-amber-400 border-amber-400' : 'border-slate-600'}`}>
                                {on && <Check size={10} className="text-amber-950" strokeWidth={3} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-slate-100 text-[12px]"><span className="font-mono text-amber-200/80">{m.matterCode}</span> {m.title}</div>
                                <div className="text-slate-500 text-[10px] mt-0.5">{m.stage} · {m.roles.join(', ')}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <div className="border-t border-white/[0.06] px-3 py-2 flex justify-between">
                        <span className="text-[10px] text-slate-500">{attached.length} attached</span>
                        <button type="button" onClick={() => setShowMatterPicker(false)} className="text-[10px] text-amber-200 hover:text-amber-100">Done</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <input value={text} onChange={(e) => setText(e.target.value)}
                placeholder={attachedMatters.length > 0 ? `Ask about ${attachedMatters.length} matter${attachedMatters.length === 1 ? '' : 's'}…` : `Message ${a.fullName.split(' ')[0]}…`}
                className="flex-1 bg-white/[0.03] border border-white/[0.08] rounded-full px-5 py-2.5 text-[13.5px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40 focus:bg-white/[0.05]"
                data-testid="chat-input" />
              <button type="submit" disabled={sending || !text.trim()}
                className="flex items-center gap-1.5 bg-gradient-to-b from-amber-400 to-amber-500 text-amber-950 font-semibold text-[13px] px-5 rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 hover:scale-[1.02] shadow-lg"
                data-testid="chat-send">
                <Send size={14} strokeWidth={2.5} />
                {sending ? '…' : 'Send'}
              </button>
            </div>
          </form>
        </div>

        {showProfile && (
          <aside className="w-80 border-l border-white/[0.06] bg-black/20 overflow-y-auto p-5 space-y-4" data-testid="agent-profile-panel">
            <div className="flex flex-col items-center text-center gap-2">
              <AgentAvatar agent={a} size={96} ring glow />
              <div>
                <div className="text-slate-50 font-medium">{a.fullName}</div>
                <div className="text-amber-200/70 text-[11px] uppercase tracking-[0.2em] mt-1">{a.designation}</div>
              </div>
              <PhotoUploader agent={a} onDone={(updated) => { setFull(updated); onAgentUpdated?.(updated); }} />
            </div>
            <div className="border-t border-white/[0.06] pt-3 space-y-3 text-[12px]">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Role</div>
                <div className="text-slate-200">{a.role}</div>
              </div>
              {a.workDefinition && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Work definition</div>
                  <div className="text-slate-300 italic leading-relaxed">{a.workDefinition}</div>
                </div>
              )}
              {a.approvedWorkAreas?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Approved work areas</div>
                  <div className="flex flex-wrap gap-1.5">
                    {a.approvedWorkAreas.map((w) => (
                      <span key={w} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">{w}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {!isBiz && (
              <div className="border-t border-white/[0.06] pt-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center justify-between">
                  <span>Related matters</span>
                  <span className="text-slate-600">{matters.length}</span>
                </div>
                {matters.length === 0 ? (
                  <div className="text-[11px] text-slate-600 italic">Not assigned to any matter yet.</div>
                ) : (
                  <div className="space-y-1.5" data-testid="related-matters">
                    {matters.map((m) => (
                      <Link key={m.id} to={`/liq/matters/${m.id}`}
                        className="block p-2 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-amber-500/30 hover:bg-amber-500/[0.03]">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-[10px] font-mono text-amber-200/80">{m.matterCode}</div>
                            <div className="text-[12px] text-slate-100 truncate">{m.title}</div>
                          </div>
                          {m.blocked && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/15 border border-rose-500/40 text-rose-300">Blocked</span>}
                        </div>
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/10 border border-sky-500/30 text-sky-300">{m.stage}</span>
                          {m.roles.map((r) => (
                            <span key={r} className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-slate-400">{r}</span>
                          ))}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

// ---------- Main Agent Hub ----------
export default function AgentHub() {
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => { api.get('/employees').then((r) => {
    const list = (r.data.items || []).filter((e) => e.entityKind === 'agent' && e.status === 'Active');
    setAgents(list);
    setSelected((prev) => prev ? (list.find((x) => x.id === prev.id) || list[0]) : list[0] || null);
  }); }, []);

  const { leadList, matterList, bizList } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (a) => !q || (a.fullName + ' ' + a.designation + ' ' + a.role).toLowerCase().includes(q);
    return {
      leadList: agents.filter((a) => a.agentTier === 'lead' && match(a)),
      matterList: agents.filter((a) => a.agentTier === 'execution' && match(a)),
      bizList: agents.filter((a) => a.agentTier === 'business' && match(a)),
    };
  }, [agents, query]);

  const Group = ({ label, icon: Icon, list, tone = 'amber' }) => (
    <div>
      <div className={`px-4 pt-4 pb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] ${
        tone === 'gold' ? 'text-amber-200/80' : tone === 'sky' ? 'text-sky-200/60' : 'text-amber-200/60'
      }`}>
        <Icon size={12} strokeWidth={2} className={tone === 'gold' ? 'text-amber-300' : tone === 'sky' ? 'text-sky-300' : 'text-amber-200/40'} /> {label}
        <span className="ml-auto text-slate-600">{list.length}</span>
      </div>
      {list.length === 0 ? (
        <div className="px-4 py-2 text-slate-600 text-[11px] italic">No matches</div>
      ) : list.map((a) => (
        <AgentListItem key={a.id} agent={a} active={selected?.id === a.id} onClick={() => setSelected(a)} />
      ))}
    </div>
  );

  return (
    <div data-testid="liq-agents">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.32em] text-amber-200/60 mb-2">The Team</div>
        <h1 className="font-serif text-[32px] leading-tight text-slate-50">Agents<span className="text-amber-300">.</span></h1>
        <p className="text-slate-400 text-[13px] mt-2 max-w-2xl">Leads manage the office and see every matter. Matter agents execute Research/Prepare/Verify on each matter. Business agents handle finance, sales and marketing with client access only.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
        <aside className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden self-start"
               style={{ boxShadow: '0 24px 60px -20px rgba(0,0,0,0.6)' }} data-testid="agent-list">
          <div className="px-4 pt-4 pb-2 border-b border-white/[0.06]">
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search agents…"
              className="w-full bg-white/[0.03] border border-white/[0.06] rounded-full px-4 py-2 text-[12px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40"
              data-testid="agent-search" />
          </div>
          <div className="max-h-[calc(100vh-19rem)] overflow-y-auto pb-3">
            <Group label="Leads" icon={Crown} list={leadList} tone="gold" />
            <div className="border-t border-white/[0.04] my-2" />
            <Group label="Matter Agents" icon={Briefcase} list={matterList} />
            <div className="border-t border-white/[0.04] my-2" />
            <Group label="Business Agents" icon={DollarSign} list={bizList} tone="sky" />
          </div>
        </aside>

        {selected ? (
          <ChatWindow key={selected.id} agent={selected}
            onAgentUpdated={(u) => setAgents((list) => list.map((x) => x.id === u.id ? { ...x, ...u } : x))} />
        ) : (
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl flex items-center justify-center h-[calc(100vh-11rem)] text-slate-500 text-sm">
            Pick an agent to start chatting.
          </div>
        )}
      </div>
    </div>
  );
}
